import React from 'react'
import { useTranslation } from 'react-i18next'

import { Carousel } from '@src/components/Carousel'
import { LinkSlide } from '@src/components/LinkSlide/LinkSlide'
import { SingleOutlet } from '@src/components/SingleOutlet/types'
import { TextClamp } from '@src/components/Text/Clamp'
import { BusinessSegment, LinkCard } from '@src/graphql-types'
import { convertToSlug } from '@src/utils/convertToSlug'
import { DateifyOutlet } from '@src/utils/fulfilmentTimes/parsers'

import { IconButton } from '../Button/IconButton'
import {
  FeaturedOutletsContainer,
  HeroContainer,
  OutletContainer,
  SliderArrowsContainer,
  HeaderLink,
  Description,
  HeaderDiv,
} from '../FeaturedOutlets/FeaturedOutlets.styles'
import { OutletSlide } from '../OutletSlide'
import { sortBy } from 'lodash'

export type SegmentWithOutletOrLinkCard = Pick<
  BusinessSegment,
  | 'id'
  | 'name'
  | 'showName'
  | 'orderBy'
  | 'description'
  | 'showDiscountLabels'
  | 'showFeaturedFlags'
  | 'type'
  | 'linkToSegmentIndex'
> & {
  cards: Array<OutletOrLinkCard>
}

export type OutletOrLinkCard =
  | (DateifyOutlet<SingleOutlet> & { __typename: string })
  | (LinkCard & { __typename: string })

type Props = {
  businessSegment: SegmentWithOutletOrLinkCard
}

export const BusinessSegmentOutlets: React.FC<Props> = ({
  businessSegment,
}) => {
  const { t } = useTranslation('navigation')
  const circleButtonLeftClassName = `${businessSegment.id}-segment-left`
  const circleButtonRightClassName = `${businessSegment.id}-segment-right`
  const cards = businessSegment.cards

  const sortedCards =
    // all other orderBys handled on backend
    businessSegment.orderBy === 'ALPHABETICAL'
      ? sortBy(cards, 'displayName')
      : cards

  return (
    <>
      {cards?.length > 0 && (
        <OutletContainer>
          <HeroContainer>
            {businessSegment.showName && (
              <>
                {businessSegment.linkToSegmentIndex &&
                !businessSegment.cards.every(
                  card => card.__typename === 'LinkCard'
                ) ? (
                  <HeaderLink
                    to={`/segments/${businessSegment.id}/${convertToSlug(
                      businessSegment.name
                    )}`}
                  >
                    <h2>{businessSegment.name}</h2>
                  </HeaderLink>
                ) : (
                  <HeaderDiv>
                    <h2>{businessSegment.name}</h2>
                  </HeaderDiv>
                )}
              </>
            )}
            <SliderArrowsContainer>
              <IconButton
                className={circleButtonLeftClassName}
                size={32}
                icon={'caretLeft'}
              />
              <IconButton
                className={circleButtonRightClassName}
                size={32}
                icon={'caretRight'}
                alt={t('right')}
              />
            </SliderArrowsContainer>
          </HeroContainer>
          {!!businessSegment.description?.length && (
            <Description>
              <TextClamp clamp={5}>{businessSegment.description}</TextClamp>
            </Description>
          )}

          <FeaturedOutletsContainer>
            <Carousel
              buttonLeftClassName={circleButtonLeftClassName}
              buttonRightClassName={circleButtonRightClassName}
              slides={sortedCards.map(card =>
                card.__typename === 'Outlet' ? (
                  <OutletSlide
                    key={`segmentOutlet-${businessSegment.id}-${card.id}`}
                    outlet={card}
                    showDiscountLabels={businessSegment.showDiscountLabels}
                    showFeaturedFlags={businessSegment.showFeaturedFlags}
                  />
                ) : (
                  <LinkSlide
                    linkCard={card as LinkCard}
                    key={`segmentLink-${card.id}`}
                  />
                )
              )}
            />
          </FeaturedOutletsContainer>
        </OutletContainer>
      )}
    </>
  )
}
